import { useState } from 'react';

import { api } from '../../api';
import { go, goReplace, hashQuery } from '../../App';
import { useLoad, usePolled } from '../../data';
import { BookingTimeline, Loading, MoneyText, PageHead, Panel, Skeleton, Stars, StatusChip, fmtMoney, formatWhen, useUndoToast } from '../../ui';
import { Account, Booking, BookingStatus } from '../../types';

const FILTERS: { key: BookingStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Needs answer' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'alternative', label: 'Alternative proposed' },
  { key: 'declined', label: 'Declined' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

// H2 — the bookings register: table, filters, detail drawer with the
// actor-stamped timeline. The drawer has a URL; browser back closes it. The
// filter state is a URL too (#/bookings?f=pending), so a notification can
// open the exact view; a chip click replaces the entry rather than pushing —
// back leaves the register, it does not walk the filters.
export function Register({ openId }: { openId?: string }) {
  const { data, error, reload } = usePolled<{ items: Booking[] }>('/bookings');
  // An undo may have removed what the drawer shows (a retracted create) —
  // land back on the list, which reloads to the truth.
  const toast = useUndoToast(() => {
    reload();
    if (openId) go('/bookings');
  });
  const f = hashQuery().get('f');
  const filter: BookingStatus | 'all' =
    FILTERS.some((x) => x.key === f) ? (f as BookingStatus | 'all') : 'all';
  const setFilter = (key: BookingStatus | 'all') =>
    goReplace(key === 'all' ? '/bookings' : `/bookings?f=${key}`);
  const account = useLoad<Account>('/account');
  const items = data ? data.items : null;

  const accepted = (booking: Booking) => {
    toast.show(
      booking,
      `New time confirmed · ${booking.productName}`,
      `${booking.supplierName} has been told.`,
    );
    reload();
  };

  const cancelled = (booking: Booking) => {
    // The cancel carries the same 30-second undo as every other action; the
    // toast is its home. Close the drawer — the row is now cancelled.
    toast.show(booking, `Cancelled · ${booking.productName}`, `${booking.supplierName ?? 'The supplier'} has been told.`);
    reload();
    go('/bookings');
  };

  // H2: the Needs-answer view is sorted by how long — longest waiting
  // first; every other view keeps the byStart order the API returns.
  const shown = (items ?? [])
    .filter((b) => filter === 'all' || b.status === filter)
    .sort((a, b) =>
      filter === 'pending' ? Date.parse(a.createdAt) - Date.parse(b.createdAt) : 0,
    );

  return (
    <div>
      <PageHead title="Bookings" sub="Every request, and where it stands.">
        <a href="#/bookings/new" className="btn" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
          New booking
        </a>
      </PageHead>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {FILTERS.map((f) => (
          <button key={f.key} className={`fchip ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="error-line">{error}</p>}
      {items === null ? (
        <Skeleton lines={6} />
      ) : shown.length === 0 ? (
        <div className="empty-neutral">
          {filter === 'all' ? 'Nothing here yet — send your first request.' : 'No bookings in this state.'}
        </div>
      ) : (
        <Panel style={{ overflow: 'hidden' }} flush>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Guest</th>
                <th>Status</th>
                <th className="num">Guest pays</th>
                <th className="num">Supplier</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((b) => (
                <tr key={b.id} className="click" onClick={() => go(`/bookings/${b.id}`)}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName ?? b.productId}
                    <br />
                    <span className="hint">{b.supplierName}</span>
                  </td>
                  <td>
                    {b.guestName}
                    {b.guestRoom ? ` · ${b.guestRoom}` : ''}
                  </td>
                  <td>
                    <StatusChip status={b.status} />
                    {/* H2: how hard the supplier has been chased, on the row. */}
                    {b.chase?.state === 'escalated' && (b.status === 'pending' || b.status === 'requested') && (
                      <span className="uchip escalated" style={{ display: 'inline-block', marginLeft: 6 }}>Escalated</span>
                    )}
                    {b.chase?.state === 'surfaced' && (b.status === 'pending' || b.status === 'requested') && (
                      <span className="uchip soon" style={{ display: 'inline-block', marginLeft: 6 }}>Being chased</span>
                    )}
                    {b.chase?.state === 'expired' && (
                      <span className="uchip escalated" style={{ display: 'inline-block', marginLeft: 6 }}>No answer</span>
                    )}
                    {/* The review prompt (DECISION-056): a completed job the hotel
                        hasn't rated yet. Computed from the booking, not stored —
                        it clears the moment the review lands. */}
                    {b.status === 'completed' && !b.review && (
                      <span className="uchip soon" style={{ display: 'inline-block', marginLeft: 6 }}>Rate supplier</span>
                    )}
                  </td>
                  <td className="num">
                    <MoneyText money={b.guestAmount} />
                  </td>
                  <td className="num">
                    <MoneyText money={b.supplierAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      {openId && (
        <Detail
          id={openId}
          tz={account.data?.timezone ?? 'Europe/Athens'}
          onAccepted={accepted}
          onCancelled={cancelled}
        />
      )}
      {toast.element}
    </div>
  );
}

function Detail({
  id,
  tz,
  onAccepted,
  onCancelled,
}: {
  id: string;
  tz: string;
  onAccepted: (b: Booking) => void;
  onCancelled: (b: Booking) => void;
}) {
  const { data: booking, error: loadError, reload } = useLoad<Booking>(`/bookings/${id}`);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const close = () => go('/bookings');

  // Cancel is allowed from requested / pending / confirmed (the transition
  // table). A plain cancel has no fee — the cancelled-with-a-fee line is P10.
  const canCancel = !!booking && ['requested', 'pending', 'confirmed'].includes(booking.status);

  const doCancel = async () => {
    if (!cancelReason.trim()) return;
    setBusy(true);
    setError('');
    try {
      const after = await api<Booking>('POST', `/bookings/${id}/cancel`, { reason: cancelReason.trim() });
      onCancelled(after);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const acceptAlternative = async () => {
    setBusy(true);
    try {
      const after = await api<Booking>('POST', `/bookings/${id}/accept-alternative`);
      // Stay busy until the refetched booking replaces the offer panel —
      // a re-enabled Accept over stale state invites a double submit.
      await reload();
      onAccepted(after);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="drawer-veil" onClick={close} />
      <aside className="drawer" role="dialog" aria-label="Booking detail">
        <div className="scroll">
          {!booking ? (
            loadError ? <p className="error-line">{loadError}</p> : <Loading />
          ) : (
            <>
              <StatusChip status={booking.status} />
              {booking.chase?.state === 'escalated' && (booking.status === 'pending' || booking.status === 'requested') && (
                <span className="uchip escalated" style={{ marginLeft: 8 }}>
                  Escalated · answer due {booking.answerDeadline ? formatWhen(booking.answerDeadline) : 'now'}
                </span>
              )}
              {booking.chase?.state === 'expired' && (
                <span className="uchip escalated" style={{ marginLeft: 8 }}>No answer by the deadline</span>
              )}
              <h1 style={{ marginTop: 12 }}>{booking.productName}</h1>
              <p className="subline">
                {booking.supplierName} · {formatWhen(booking.start)}
              </p>

              <div className="kv">
                <div className="kvrow">
                  <span className="k">Guest</span>
                  <span className="v">
                    {booking.guestName}
                    {booking.guestRoom ? ` · ${booking.guestRoom}` : ''}
                  </span>
                </div>
                <div className="kvrow">
                  <span className="k">Party</span>
                  <span className="v">
                    {booking.guests.adults} adults{booking.guests.children ? ` · ${booking.guests.children} children` : ''}
                  </span>
                </div>
                <div className="kvrow">
                  <span className="k">Supplier receives</span>
                  <span className="v">
                    <MoneyText money={booking.supplierAmount} />
                  </span>
                </div>
                <div className="kvrow">
                  <span className="k">Hotel margin</span>
                  <span className="v">
                    <MoneyText money={booking.margin} />
                  </span>
                </div>
                <div className="kvrow earn">
                  <span className="k">Guest pays</span>
                  <span className="v">
                    <MoneyText money={booking.guestAmount} />
                  </span>
                </div>
                {/* The cancellation fee (P10) once it was charged — a cost to the
                    hotel, its own line, never folded into the margin above. */}
                {booking.cancelFee && (
                  <div className="kvrow">
                    <span className="k">Cancellation fee paid</span>
                    <span className="v">
                      <MoneyText money={booking.cancelFee} />
                    </span>
                  </div>
                )}
              </div>

              {booking.status === 'alternative' && booking.proposedStart && (
                <Panel style={{ background: 'var(--color-primary-light)', borderColor: 'var(--color-primary-border)', marginBottom: 20 }}>
                  <h2 style={{ fontSize: 'var(--text-lg)', color: 'var(--text-primary)', marginBottom: 6 }}>
                    The supplier offers {formatWhen(booking.proposedStart)}
                  </h2>
                  <p className="hint" style={{ marginBottom: 12 }}>
                    The slot is held for 12 hours, then this reverts to Requested.{' '}
                    <a href={`#/alternatives/${booking.id}`}>Compare side by side →</a>
                  </p>
                  <button className="btn" disabled={busy} onClick={acceptAlternative}>
                    Accept {formatWhen(booking.proposedStart)}
                  </button>
                </Panel>
              )}

              <BookingTimeline timeline={booking.timeline} tz={tz} />

              {/* The review (DECISION-056): the hotel rates the supplier once the
                  job is completed. The CTA is a STATE of this detail — a
                  completed booking not yet rated — and it clears the moment the
                  review lands (the same reload that shows it back). */}
              {booking.status === 'completed' && (
                <ReviewSection booking={booking} onRated={reload} />
              )}
              {cancelling && (
                <div className="field" style={{ marginTop: 16 }}>
                  <label htmlFor="creason">Why is this being cancelled?</label>
                  <textarea id="creason" rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} autoFocus />
                  <p className="hint">The supplier is told when the 30 seconds lapse — undo before then and they never hear of it.</p>
                  {/* The cancel preview (P10): a confirmed job inside the window
                      owes the supplier a fee. Server-computed, so it is exactly
                      what the cancel will charge. Undo reverses it too. */}
                  {booking.cancelFeePreview && (
                    <p className="hint" style={{ color: 'var(--color-warning-dark)', marginTop: 8 }}>
                      This confirmed job is within the cancellation window — cancelling now owes{' '}
                      <b>{fmtMoney(booking.cancelFeePreview)}</b> to the supplier for the held slot. It is
                      its own line, never netted; undo before the 30 seconds lapse and nothing is owed.
                    </p>
                  )}
                </div>
              )}
              {(error || loadError) && <p className="error-line">{error || loadError}</p>}
            </>
          )}
        </div>
        <div className="pinned">
          {cancelling ? (
            <>
              <button
                className="btn secondary"
                disabled={busy}
                onClick={() => { setCancelling(false); setCancelReason(''); setError(''); }}
                style={{ flex: 1 }}
              >
                Keep it
              </button>
              <button
                className="btn danger-solid"
                disabled={busy || !cancelReason.trim()}
                onClick={doCancel}
                style={{ flex: 2 }}
              >
                {busy ? 'Cancelling…' : 'Confirm cancellation'}
              </button>
            </>
          ) : (
            <>
              <button className="btn secondary" onClick={close} style={{ flex: 1 }}>
                Close
              </button>
              {canCancel && (
                <button className="btn danger-outline" onClick={() => setCancelling(true)} style={{ flex: 1 }}>
                  Cancel booking
                </button>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

// The review of a completed job (DECISION-056). Once rated it reads back the
// stars and the note; until then it is the "rate the supplier" CTA — a star
// picker and an optional note, posting to the SAME booking. One review per
// booking: the API rejects a second, and the read-back replaces the form.
function ReviewSection({ booking, onRated }: { booking: Booking; onRated: () => Promise<void> | void }) {
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (booking.review) {
    return (
      <Panel style={{ marginTop: 16 }}>
        <h2 style={{ fontSize: 'var(--text-lg)', color: 'var(--text-secondary)', marginBottom: 8 }}>Your review</h2>
        <Stars rating={booking.review.rating} />
        {booking.review.note && <p className="review-note" style={{ marginTop: 8 }}>“{booking.review.note}”</p>}
      </Panel>
    );
  }

  const submit = async () => {
    if (rating < 1) return;
    setBusy(true);
    setError('');
    try {
      await api('POST', `/bookings/${booking.id}/review`, { rating, ...(note.trim() ? { note: note.trim() } : {}) });
      await onRated();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Panel style={{ marginTop: 16 }}>
      <h2 style={{ fontSize: 'var(--text-lg)', color: 'var(--text-secondary)', marginBottom: 4 }}>Rate {booking.supplierName ?? 'the supplier'}</h2>
      <p className="hint" style={{ marginBottom: 10 }}>The job is done. How did it go? Your rating shows on their profile.</p>
      <div className="star-pick" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`star-btn${n <= rating ? ' on' : ''}`}
            role="radio"
            aria-checked={n === rating}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            onClick={() => setRating(n)}
          >
            ★
          </button>
        ))}
      </div>
      <div className="field" style={{ marginTop: 12 }}>
        <label htmlFor="rnote">Add a note (optional)</label>
        <textarea id="rnote" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="On time, great with the guests…" />
      </div>
      {error && <p className="error-line" role="alert">{error}</p>}
      <button className="btn" disabled={busy || rating < 1} onClick={submit} style={{ marginTop: 4 }}>
        {busy ? 'Saving…' : 'Submit review'}
      </button>
    </Panel>
  );
}
